import { enhancedApiClient, isSuccessResponse, ApiResponse } from './enhancedApiClient';
import { MasterOption } from '../components/common/MasterDropdown';

// Re-export MasterOption for convenience
export type { MasterOption };

export interface MasterDropdownResponse {
  success: boolean;
  data: Array<{
    [key: string]: string | number;
  }>;
}

class MasterDropdownService {
  /**
   * Get states dropdown data
   */
  async getStatesDropdown(): Promise<MasterOption[]> {
    try {
      const response = await enhancedApiClient.get('/getDropdown/states');
      console.log('States dropdown response:', response);
      if (isSuccessResponse(response)) {
        // Response structure: response.data = { success: true, data: [...] }
        // Backend returns snake_case: { state_id, state_name }
        const responseData = response.data?.data || response.data || [];
        console.log('States dropdown data:', responseData);
        const options = responseData.map((item: any) => ({
          value: item.stateName || item.state_name || item.name || String(item.value || ''),
          label: item.stateName || item.state_name || item.name || String(item.value || ''),
        })).filter((item: any) => item.value && item.label);
        console.log('States dropdown options:', options);
        return options;
      }
      console.warn('States dropdown response not successful:', response);
      return [];
    } catch (error) {
      console.error('Error fetching states dropdown:', error);
      return [];
    }
  }

  /**
   * Get cities dropdown data
   * @param stateIdOrName Optional state ID (number) or state name (string) to filter cities
   */
  async getCitiesDropdown(stateIdOrName?: number | string): Promise<MasterOption[]> {
    try {
      // If it's a number, use state_id; if it's a string, use state_name
      const params = stateIdOrName 
        ? (typeof stateIdOrName === 'number' 
            ? { state_id: stateIdOrName } 
            : { state_name: stateIdOrName })
        : {};
      const response = await enhancedApiClient.get('/getDropdown/cities', { params });
      console.log('Cities dropdown response:', response);
      if (isSuccessResponse(response)) {
        // Response structure: response.data = { success: true, data: [...] }
        // Backend returns snake_case: { city_id, city_name }
        const responseData = response.data?.data || response.data || [];
        console.log('Cities dropdown data:', responseData);
        const options = responseData.map((item: any) => ({
          value: item.cityName || item.city_name || item.name || String(item.value || ''),
          label: item.cityName || item.city_name || item.name || String(item.value || ''),
          stateName: item.stateName || item.state_name,
          stateId: item.stateId || item.state_id,
        })).filter((item: any) => item.value && item.label);
        console.log('Cities dropdown options:', options);
        return options;
      }
      console.warn('Cities dropdown response not successful:', response);
      return [];
    } catch (error) {
      console.error('Error fetching cities dropdown:', error);
      return [];
    }
  }

  /**
   * Get colors dropdown data
   */
  async getColorsDropdown(): Promise<MasterOption[]> {
    try {
      const response = await enhancedApiClient.get('/getDropdown/colors');
      console.log('Colors dropdown response:', response);
      if (isSuccessResponse(response)) {
        // Response structure: response.data = { success: true, data: [...] }
        // Backend returns snake_case: { color_id, color_name }
        const responseData = response.data?.data || response.data || [];
        console.log('Colors dropdown data:', responseData);
        const options = responseData.map((item: any) => ({
          value: item.colorName || item.color_name || item.name || String(item.value || ''),
          label: item.colorName || item.color_name || item.name || String(item.value || ''),
        })).filter((item: any) => item.value && item.label);
        console.log('Colors dropdown options:', options);
        return options;
      }
      console.warn('Colors dropdown response not successful:', response);
      return [];
    } catch (error) {
      console.error('Error fetching colors dropdown:', error);
      return [];
    }
  }

  /**
   * Get Sets dropdown data
   */
  async getSetsDropdown(): Promise<MasterOption[]> {
    try {
      const response = await enhancedApiClient.get('/getDropdown/sets');
      console.log('Set dropdown response:', response);
      if (isSuccessResponse(response)) {
        // Response structure: response.data = { success: true, data: [...] }
        // Backend returns snake_case: { color_id, color_name }
        const responseData = response.data?.data || response.data || [];
        console.log('Sets dropdown data:', responseData);
        const options = responseData.map((item: any) => ({
          value: item.SetName || item.set_name || item.name || String(item.value || ''),
          label: item.SetName || item.set_name || item.name || String(item.value || ''),
        })).filter((item: any) => item.value && item.label);
        console.log('Sets dropdown options:', options);
        return options;
      }
      console.warn('Sets dropdown response not successful:', response);
      return [];
    } catch (error) {
      console.error('Error fetching sets dropdown:', error);
      return [];
    }
  }

  /**
   * Get Items dropdown data
   */
  async getItemsDropdown(): Promise<MasterOption[]> {
    try {
      const response = await enhancedApiClient.get('/getDropdown/items');
      console.log('Item dropdown response:', response);
      if (isSuccessResponse(response)) {
        // Response structure: response.data = { success: true, data: [...] }
        // Backend returns snake_case: { color_id, color_name }
        const responseData = response.data?.data || response.data || [];
        console.log('Item dropdown data:', responseData);
        const options = responseData.map((item: any) => ({
          value: item.itemName || item.item_name || item.name || String(item.value || ''),
          label: item.itemName || item.item_name || item.name || String(item.value || ''),
        })).filter((item: any) => item.value && item.label);
        console.log('Items dropdown options:', options);
        return options;
      }
      console.warn('Items dropdown response not successful:', response);
      return [];
    } catch (error) {
      console.error('Error fetching items dropdown:', error);
      return [];
    }
  }

  /**
   * Get state information by city name or city ID
   * @param cityNameOrId City name (string) or city ID (number)
   */
  async getStateByCity(cityNameOrId: string | number): Promise<{ stateName?: string; stateId?: number } | null> {
    try {
      const params = typeof cityNameOrId === 'number' 
        ? { city_id: cityNameOrId } 
        : { city_name: cityNameOrId };
      const response = await enhancedApiClient.get('/getDropdown/stateByCity', { params });
      console.log('State by city response:', response);
      if (isSuccessResponse(response)) {
        const data = response.data?.data || response.data;
        if (data && data.stateName) {
          return {
            stateName: data.stateName || data.state_name,
            stateId: data.stateId || data.state_id,
          };
        }
      }
      console.warn('State by city response not successful:', response);
      return null;
    } catch (error) {
      console.error('Error fetching state by city:', error);
      return null;
    }
  }

  /**
   * Get role types dropdown data
   * Uses role_name as value (for code) and display_name as label (for display)
   */
  async getRoleTypesDropdown(): Promise<MasterOption[]> {
    try {
      const response = await enhancedApiClient.get('/getDropdown/role-types');
      console.log('Role types dropdown response:', response);
      if (isSuccessResponse(response)) {
        // Response structure: response.data = { success: true, data: [...] }
        // Backend returns: { role_type_id, role_name, display_name }
        const responseData = response.data?.data || response.data || [];
        console.log('Role types dropdown data:', responseData);
        const options = responseData.map((item: any) => ({
          // Use role_name as value (code value used in system)
          value: item.roleName || item.role_name || item.name || String(item.value || ''),
          // Use display_name as label (user-friendly display name)
          label: item.displayName || item.display_name || item.roleName || item.role_name || item.name || String(item.value || ''),
        })).filter((item: any) => item.value && item.label);
        console.log('Role types dropdown options:', options);
        return options;
      }
      console.warn('Role types dropdown response not successful:', response);
      return [];
    } catch (error) {
      console.error('Error fetching role types dropdown:', error);
      return [];
    }
  }

  /**
   * Get locations dropdown data
   */
  async getLocationsDropdown(): Promise<MasterOption[]> {
    try {
      const response = await enhancedApiClient.get('/getDropdown/locations');
      console.log('Locations dropdown response:', response);
      if (isSuccessResponse(response)) {
        // Response structure: response.data = { success: true, data: [...] }
        // Backend returns snake_case: { location_id, location_name }
        const responseData = response.data?.data || response.data || [];
        console.log('Locations dropdown data:', responseData);
        const options = responseData.map((item: any) => ({
          value: item.locationName || item.location_name || item.name || String(item.value || ''),
          label: item.locationName || item.location_name || item.name || String(item.value || ''),
        })).filter((item: any) => item.value && item.label);
        console.log('Locations dropdown options:', options);
        return options;
      }
      console.warn('Locations dropdown response not successful:', response);
      return [];
    } catch (error) {
      console.error('Error fetching locations dropdown:', error);
      return [];
    }
  }
}

export const masterDropdownService = new MasterDropdownService();

